import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatest, EMPTY, forkJoin, map, Subject, switchMap, tap } from 'rxjs';

import { EpdDashboardContentComponent } from './components/epd-dashboard-content/epd-dashboard-content.component';
import { EpdDashboardHeaderComponent } from './components/epd-dashboard-header/epd-dashboard-header.component';
import { EpdExportModalComponent } from './components/epd-export-modal/epd-export-modal.component';
import { EpdManagementCardComponent } from './components/epd-management-card/epd-management-card.component';
import { EpdPepPreviewComponent } from './components/epd-pep-preview/epd-pep-preview.component';

import { CookiesService } from '@services/cookies.service';
import { ComposerApiService } from '@services/composer-api.service';
import { MattermapApiService } from '@services/mattermap-api.service';
import { ToastrService } from '@services/toaster.service';
import { ToolbarEventService } from '@services/toolbar-event.service';
import { EpdDashboardService } from './services/epd-dashboard.service';

import { LoaderModule } from '@mds/ui';

import Highcharts from 'highcharts';

import { HttpErrorResponse } from '@angular/common/http';
import { InviteShareComponent } from '@dialogs/share-invite/share-invite-dialog.component';
import { MdsIcon } from '@mds/icons';
import {
  EPD_STANDARDS,
  EpdFamilyStatus,
  Epdresponse,
  Export,
  PCR,
  seENVs,
  Standard
} from '@pages/dashboards/epd-dashboard/utils/epd-dashboard.model';
import { AnalyzeLayersService } from '@services/analyze.layers.service';
import { AnalyzeService } from '@services/analyze.service';
import {
  EPDManagementService,
  EPDMenuSection,
  EPDRowActionSection,
  getResumeFormSchema
} from '@services/epd-management.service';
import { EventDelegatorService } from '@services/event-delegator.service';
import { GlobalDataService } from '@services/global-data.service';
import { MenuService } from '@services/menu.service';
import { ReusableService } from '@services/reusable.service';
import { getEntityTypeIcon } from '@utils/entity-type-icon';
import { EPD_CLIMATE_CHART_OPTIONS, EPD_MATERIAL_CHART_OPTIONS } from './utils/chart-options';
import { collectChangedValues, triggerBlobDownload } from './utils/epd-dashboard-helpers';
import { largestRemainderRound } from './utils/largest-remainder-round';

enum EPDMenuActions {
  SHARE = 'share',
  INVITE = 'invite',
  EDIT = 'edit',
  EXPORT_PEP = 'pep_export',
  EXPORT_PIM = 'pim_import',
  EXPORT_EXCEL = 'excel_export',
  EXPORT_PDF = 'pdf_export',
  EXPORT_WORD = 'word_export',
  EXPORT_XML = 'xml_export',
  SAVE = 'save'
}

const EPD_MANAGEMENT_STANDARDS: EPD_STANDARDS[] = [
  EPD_STANDARDS.PEP_ECOPASSPORT,
  EPD_STANDARDS.IBU,
  EPD_STANDARDS.NORGE
];

@Component({
  selector: 'app-epd-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    EpdDashboardHeaderComponent,
    EpdDashboardContentComponent,
    EpdManagementCardComponent,
    LoaderModule
  ],
  providers: [EpdDashboardService],
  templateUrl: './epd-dashboard.component.html',
  styleUrls: ['./epd-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EpdDashboardComponent implements OnInit {
  updateData$ = new Subject<void>();
  fetchData$ = new Subject<void>();
  fetchLanguage$ = new Subject<void>();
  showPreview = false;
  canPreview: Export | null = null;
  Highcharts = Highcharts;
  rid: string;
  isLoading = true;
  isSEColorThemeEnabled = false;
  epdFamilyStatus: EpdFamilyStatus;
  statusFormSchema: EPDRowActionSection['formSchema'] = [];
  resumeFormSchema: EPDMenuSection['formSchema'] = [];
  tableData: any = [];
  section: any = [];
  chartsData: any = [];
  productName: string;
  selectedPcr: PCR;
  data;
  payload = null;
  protected router = inject(Router);
  protected dialog = inject(MatDialog);
  protected destroyRef = inject(DestroyRef);
  protected cdr = inject(ChangeDetectorRef);
  protected epdDashboardService = inject(EpdDashboardService);
  protected toastrService = inject(ToastrService);
  protected cookiesService = inject(CookiesService);
  protected activatedRoute = inject(ActivatedRoute);
  protected reusableService = inject(ReusableService);
  protected mattermapApiService = inject(MattermapApiService);
  protected composerApiService = inject(ComposerApiService);
  protected toolbarEventService = inject(ToolbarEventService);
  protected menuService = inject(MenuService);
  protected analyzeService = inject(AnalyzeService);
  protected epdManagementService = inject(EPDManagementService);
  protected globalDataService = inject(GlobalDataService);
  protected analyzeLayersService = inject(AnalyzeLayersService);
  protected eventDelegatorService = inject(EventDelegatorService);
  protected pcrList: any = [];
  protected methodList: Standard[];
  protected languages: any[] | null;
  protected selectedLanguage;
  protected selectedMethod: Standard;
  protected familyRid: string | null = null;

  get showEpdManagement(): boolean {
    if (!this.familyRid) {
      return EPD_MANAGEMENT_STANDARDS.includes(this.selectedMethod?.value as EPD_STANDARDS);
    }
    return this.shouldShowEpdManagement(this.selectedMethod?.value, this.epdFamilyStatus);
  }

  private readonly menuActionHandlers: Record<EPDMenuActions, () => void> = {
    [EPDMenuActions.SHARE]: () => this.showShare(),
    [EPDMenuActions.INVITE]: () => this.invite(),
    [EPDMenuActions.EDIT]: () => this.openComposer(),
    [EPDMenuActions.EXPORT_PEP]: () => this.additionalExports(EPDMenuActions.EXPORT_PEP),
    [EPDMenuActions.EXPORT_PIM]: () => this.additionalExports(EPDMenuActions.EXPORT_PIM),
    [EPDMenuActions.EXPORT_EXCEL]: () => this.additionalExports(EPDMenuActions.EXPORT_EXCEL),
    [EPDMenuActions.EXPORT_PDF]: () => this.additionalExports(EPDMenuActions.EXPORT_PDF),
    [EPDMenuActions.EXPORT_XML]: () => this.additionalExports(EPDMenuActions.EXPORT_XML),
    [EPDMenuActions.EXPORT_WORD]: () => this.exportEPD(EPDMenuActions.EXPORT_WORD),
    [EPDMenuActions.SAVE]: () => this.updateData$.next()
  };
  private readonly megamenuActionHandlers: Record<string, () => void> = {
    share: () => this.showShare(),
    invite: () => this.invite(),
    compose: () => this.openComposer(),
    export_pdf: () => this.additionalExports(EPDMenuActions.EXPORT_PDF),
    export_xml: () => this.additionalExports(EPDMenuActions.EXPORT_XML),
    export_excel: () => this.additionalExports(EPDMenuActions.EXPORT_EXCEL),
    export_pim_import: () => this.additionalExports(EPDMenuActions.EXPORT_PIM),
    export_pep_export: () => this.additionalExports(EPDMenuActions.EXPORT_PEP),
    export_docx: () => this.exportEPD(EPDMenuActions.EXPORT_WORD),
    save: () => this.updateData$.next()
  };

  get previewButtonLabel() {
    return this.payload ? 'Save & Export' : 'Preview & Export';
  }

  get standardLogo() {
    return this.data?.pdf_export_data?.standard_logo || null;
  }

  ngOnInit(): void {
    // Enable SE color theme for charts for SE Sandboxes
    const isSEThemeEnabled = seENVs.includes(window.location.hostname);
    if (isSEThemeEnabled) this.cookiesService.setCookie('epd_se_theme_enabled', true);

    if (this.cookiesService.getCookie('epd_se_theme_enabled') === 'true') {
      this.isSEColorThemeEnabled = true;
    }

    combineLatest([
      this.activatedRoute.paramMap,
      this.activatedRoute.queryParamMap,
      this.epdDashboardService.fetchStandardsList()
    ])
      .pipe(
        map(([params, queryParams, methods]) => ({
          rid: params.get('rid'),
          method: queryParams.get('method'),
          methods
        })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ rid, method, methods }) => {
        this.rid = rid;
        this.methodList = methods;

        // set default
        if (!method) {
          method = EPD_STANDARDS.EN_15804_A2;
        }
        this.selectedMethod = this.methodList.find((standard: Standard) => standard.value === method);
        this.initToolbar();
        if (!this.selectedMethod) {
          this.toastrService.showMessage('No standard found');
          this.router.navigate([`/dashboard`]);
        } else {
          this.fetchData$.next();
        }
      });

    this.updateData$
      .pipe(
        switchMap(() => this.epdDashboardService.submitData(encodeURI(this.rid.replace('#', '')), this.payload)),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY)
      )
      .subscribe((res) => {
        this.payload = null;
        this.menuService.updateMenuItem('save', { visible: false });
        this.toolbarEventService.updateMegaMenuActions.next({ save: false });
        this.fetchData$.next();
      });

    this.fetchData$
      .pipe(
        tap(() => {
          this.isLoading = true;
        }),
        switchMap(() =>
          this.epdDashboardService.fetchData(this.rid, this.selectedPcr?.rid, this.selectedMethod?.value)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res: Epdresponse) => {
          this.processNewEPD(res);
          if (EPD_MANAGEMENT_STANDARDS.includes(res.data.standard)) {
            this.familyRid = res.data.taxonomy_family_rid;
            this.loadEpdManagementSection();
          }
          if (this.showPreview) {
            this.previewAndExport();
          }
          this.updateMenu();
          this.isLoading = false;
          this.cdr.markForCheck();
          this.fetchLanguage$.next();
        },
        error: () => {
          this.router.navigate(['/dashboard']);
        }
      });

    this.fetchLanguage$
      .pipe(
        switchMap(() => {
          this.languages = null;
          return this.epdDashboardService.fetchLanguagesList(this.selectedMethod?.value);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((languages) => {
        this.languages = languages;
        if (languages?.length > 1) this.selectedLanguage = languages.find((lang) => lang.key === 'en');
      });

    this.subscribeToMenuEvent();
  }

  getMenuActions(event: { action: { key: EPDMenuActions } }) {
    this.menuActionHandlers[event.action.key]?.();
  }

  resetToolbar() {
    const allActionsValidation = {
      show: ['share', 'invite', 'compose'],
      hidden: ['save'],
      disabled: []
    };

    const allActions = {
      action: [
        { key: 'share', value: 'Share' },
        { key: 'invite', value: 'Invite' },
        { key: 'compose', value: 'Compose' }
      ],
      additionalActivity: [
        { key: 'export_pdf', value: 'Export PDF' },
        { key: 'export_xml', value: 'Export XML' },
        { key: 'export_docx', value: 'Export Word' },
        { key: 'export_excel', value: 'Export Excel' },
        { key: 'export_pim_import', value: 'PIM Import' },
        { key: 'export_pep_export', value: 'PEP Export' }
      ],
      commonActivity: [{ key: 'save', value: 'Save' }],
      validations: allActionsValidation
    };
    this.globalDataService.setAllActions(allActions);
  }

  onEditSectionUpdated(event) {
    // for edit section models
    const section = this.data.sections.find((s: any) => s.key === event.key);
    if (!section.value) {
      section.value = event.value;
      return;
    }

    const updated = collectChangedValues(section.value, event.value);

    if (updated) {
      this.payload = updated;
      this.menuService.updateMenuItem('save', { visible: true });
      this.toolbarEventService.updateMegaMenuActions.next({ save: true });
    }
  }

  onChangeMethodology(methods: Standard[]) {
    [this.selectedMethod] = methods;
    this.epdFamilyStatus = undefined;
    this.fetchData$.next();
  }

  onChangePcr(selected: PCR[]) {
    [this.selectedPcr] = selected;
    this.fetchData$.next();
  }

  onChangeLanguage(language: any[]) {
    [this.selectedLanguage] = language;
    this.cdr.markForCheck();
  }

  shouldShowEpdManagement(
    selectedStandard: EPD_STANDARDS,
    familyStatus: Pick<EpdFamilyStatus, 'status' | 'epd_standard'> | null | undefined
  ): boolean {
    if (!EPD_MANAGEMENT_STANDARDS.includes(selectedStandard) || !familyStatus) {
      return false;
    }

    if (familyStatus.epd_standard) {
      return familyStatus.epd_standard === selectedStandard;
    }

    return familyStatus.status === 'EPD not initiated';
  }

  initChart(chartsData) {
    const TOP_N = 7;
    const rawItems = chartsData[0].items.map((row) => ({
      name: (row.category || row.top_level_category) as string,
      pct: (row.percentage as number) * 100
    }));
    rawItems.sort((a, b) => b.pct - a.pct);

    // Separate named categories from any backend "Others" items
    const named = rawItems.filter((r) => r.name !== 'Others');
    const othersPct = rawItems.filter((r) => r.name === 'Others').reduce((sum, r) => sum + r.pct, 0);
    const shown = named.slice(0, TOP_N);
    const overflowPct = named.slice(TOP_N).reduce((sum, r) => sum + r.pct, 0) + othersPct;
    if (overflowPct > 0) {
      shown.push({ name: 'Others', pct: overflowPct });
    }

    const roundedPcts = largestRemainderRound(shown.map((r) => r.pct));
    const chart1Data = shown.map((row, i) => ({
      name: row.name,
      y: roundedPcts[i]
    }));

    const chartCategories = [];
    const chart2Data: any = [
      {
        colorByPoint: true,
        data: []
      }
    ];
    chartsData[1].items.forEach((row) => {
      chartCategories.push(row.category || row.top_level_category);
      chart2Data[0].data.push(+row.percentage.toFixed(2));
    });

    this.chartsData = [
      {
        title: chartsData[0].title,
        options: {
          ...EPD_MATERIAL_CHART_OPTIONS,
          colors: this.isSEColorThemeEnabled
            ? [
                '#6fa4a7',
                '#4ea084',
                '#259b57',
                '#009231',
                '#008735',
                '#007c39',
                '#00713d',
                '#006740',
                '#005c44',
                '#005148',
                '#00474b',
                '#003c4f'
              ]
            : EPD_MATERIAL_CHART_OPTIONS.colors,
          series: [
            {
              type: 'pie',
              data: chart1Data
            }
          ]
        }
      },
      {
        title: chartsData[1].title,
        options: {
          ...EPD_CLIMATE_CHART_OPTIONS,
          colors: this.isSEColorThemeEnabled
            ? ['#6fa4a7', '#1b994d', '#008337', '#006b3f', '#005447', '#003c4f']
            : EPD_CLIMATE_CHART_OPTIONS.colors,
          xAxis: {
            categories: chartCategories
          },
          series: chart2Data
        }
      }
    ];
  }

  downloadAttachment(url) {
    this.epdDashboardService.downloadAdditionalExports(url).subscribe(
      (response) => {
        triggerBlobDownload(response);
      },
      (error: HttpErrorResponse) => {
        void this.handleDownloadError(error);
      }
    );
  }

  private async handleDownloadError(error: HttpErrorResponse) {
    try {
      const body = JSON.parse(await error.error.text());
      if (body.message) {
        this.toastrService.show(body.message, 5000);
        return;
      }
    } catch {}

    this.toastrService.show('Download failed', 3000);
  }

  previewAndExport() {
    if (this.payload) {
      this.showPreview = true;
      this.updateData$.next();
      return;
    }

    let { url } = this.canPreview;
    if (!url) return;

    if (this.selectedLanguage) {
      url += `&language=${this.selectedLanguage.key}`;
    }

    this.data.charts = this.chartsData;
    this.isLoading = true;

    this.dialog
      .open(EpdPepPreviewComponent, {
        data: { url },
        panelClass: 'standard-dialog'
      })
      .afterClosed()
      .subscribe((res) => {
        this.cdr.markForCheck();
        this.isLoading = false;
      });
  }

  private initToolbar() {
    this.composerApiService
      .getTileCards(this.rid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((products: any) => {
        this.setMenu(products[0]);
      });

    this.resetToolbar();
    this.globalDataService.setActiveApp(undefined);
    const layers = this.mattermapApiService.getMattermapLayers();
    const product = this.composerApiService.getTileCards(this.rid);
    forkJoin([layers, product]).subscribe(([layers, products]: [any, any]) => {
      const layerList = layers.layers;
      const app = layerList
        .find((e) => e.category === 'Business')
        .list.find((e) => e.layer === 'Epd-dashboard' || e.module === 'epd-dashboard');
      this.globalDataService.setMegaMenuList(layerList);
      this.globalDataService.setSelectedApp(app);
      this.globalDataService.setActiveApp(app);
    });

    this.globalDataService.setUrlParam({ rid: this.rid });
    this.globalDataService.setCurrentActiveApp({ name: 'app.epd-dashboard' });
    this.analyzeService.setAnalyzeRid(this.rid);
  }

  private processNewEPD(res: Epdresponse) {
    this.pcrList = res.available_pcrs.map((pcr) => ({
      ...pcr,
      text: pcr.name,
      value: pcr.rid,
      updated: new Date()
    }));
    this.section = res.data.sections;
    this.initChart(res.data.charts);
    this.data = res.data;
    this.productName = res.data.metadata.name;
    this.selectedPcr = res.data.pcr;
    this.tableData = Object.entries(res.data.tables).map(([a, v]) => ({ ...v, printLayout: 'autoHeight' }));
    this.canPreview = (res.data.available_exports || []).find((exp) => exp.key === EPDMenuActions.EXPORT_PDF);
    const available_exports: string[] = (res.data.available_exports || []).map((exp) => exp.key);
    const isPimExportEnabled = available_exports.includes('pim_import');
    const isPepExportEnabled = available_exports.includes('pep_export');
    const isExcelExportEnabled = available_exports.includes('excel_export');
    const isXMLExportEnabled = available_exports.includes('xml_export');
    this.toolbarEventService.updateMegaMenuActions.next({ reset: true });
    this.toolbarEventService.updateMegaMenuActions.next({
      share: true,
      invite: true,
      compose: true,
      export_pep_export: isPepExportEnabled,
      export_pim_import: isPimExportEnabled,
      export_excel: isExcelExportEnabled,
      export_xml: isXMLExportEnabled,
      export_pdf: this.canPreview
    });
    this.cdr.markForCheck();
  }

  private loadEpdManagementSection(): void {
    this.epdFamilyStatus = undefined;
    this.statusFormSchema = [];
    this.resumeFormSchema = [];
    if (!this.familyRid) return;

    this.epdManagementService
      .fetchEPDFamilyStatus(this.familyRid.replace('#', ''))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY)
      )
      .subscribe((response) => {
        this.statusFormSchema = response.formSchema.row_actions.status.formSchema;
        this.resumeFormSchema = getResumeFormSchema(response.formSchema.menus);
        this.epdFamilyStatus = {
          ...response.data,
          version: `${String(response.data.version_major).padStart(2, '0')}.${String(response.data.version_minor).padStart(2, '0')}`
        };
        this.cdr.markForCheck();
      });
  }

  private setMenu(entity: any) {
    this.menuService.setMenuConfig({
      activeApp: {
        mainIcon: {
          icon: MdsIcon.APP_EPD_DASHBOARD
        },
        name: 'EPD Dashboard',
        icons: [],
        subtitle: '',
        menuTrigger: 'selectApps'
      },
      activeEntity: {
        mainIcon: {
          icon: MdsIcon.PRODUCT_GROUP,
          pathColors: ['#fff']
        },
        rid: entity?.rid,
        name: entity?.title,
        icons: [{ key: getEntityTypeIcon(entity.stage) }],
        subtitle: entity.card_type_label
      },
      centerActions: [],
      rightActions: [
        {
          label: 'Edit',
          key: EPDMenuActions.EDIT,
          icon: MdsIcon.EDIT,
          selected: false,
          disabled: false,
          visible: true
        },
        {
          label: 'Save',
          key: EPDMenuActions.SAVE,
          icon: MdsIcon.SAVE,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'PEP Export',
          key: EPDMenuActions.EXPORT_PEP,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'PIM Import',
          key: EPDMenuActions.EXPORT_PIM,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'Export PDF',
          key: EPDMenuActions.EXPORT_PDF,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'Export Excel',
          key: EPDMenuActions.EXPORT_EXCEL,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'Export Word',
          key: EPDMenuActions.EXPORT_WORD,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'Export XML',
          key: EPDMenuActions.EXPORT_XML,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'Share',
          key: EPDMenuActions.SHARE,
          icon: MdsIcon.SHARE,
          selected: false,
          disabled: false,
          visible: true
        },
        {
          label: 'Invite',
          key: EPDMenuActions.INVITE,
          icon: MdsIcon.INVITE,
          selected: false,
          disabled: false,
          visible: true
        }
      ]
    });
  }

  private updateMenu() {
    const unchangedActions = [EPDMenuActions.EDIT, EPDMenuActions.SHARE, EPDMenuActions.INVITE];
    const { rightActions } = this.menuService.menuConfigSubject.getValue();
    const availableExports = [
      ...this.data.available_exports.map((availableExport: any) => availableExport.key),
      ...unchangedActions
    ];
    const updatedActions = (rightActions || []).map((action) => {
      return { [action.key]: { visible: availableExports.includes(action.key as EPDMenuActions) } };
    });
    this.menuService.updateMenuItems(updatedActions);
    this.cdr.markForCheck();
  }

  private subscribeToMenuEvent() {
    this.menuService.reload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((reload: boolean) => {
      if (reload) {
        this.fetchData$.next();
      }
    });
    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value) {
        this.getMenuActions({ action: value });
      }
    });
  }

  private showShare() {
    const userInfo = JSON.parse(localStorage.getItem('user_info'));
    const link = document.location.pathname;
    const data = {
      type: 'showInvite',
      value: 'Share',
      data: {
        fr: userInfo.email,
        link,
        rid: this.rid,
        usersLackingAccessList: ''
      }
    };

    this.dialog.open(InviteShareComponent, {
      data,
      panelClass: 'show-invite'
    });
  }

  private invite() {
    this.dialog.open(InviteShareComponent, {
      data: { type: 'showInvite', value: 'Invite' },
      panelClass: 'show-invite'
    });
  }

  private openComposer() {
    this.router.navigate([`/mattermaps/overview/${this.rid}/edit`]);
  }

  private additionalExports(action: EPDMenuActions) {
    // Display dialog when sections exists, else download file from endpoint
    const availableExports: any[] = this.data.available_exports || [];
    let data: any;
    if ([EPDMenuActions.EXPORT_PIM, EPDMenuActions.EXPORT_PEP].includes(action)) {
      const exportSection: Export = availableExports.find((ex) => ex.key === action);
      data = { ...exportSection, rid: this.rid };
    } else if ([EPDMenuActions.EXPORT_EXCEL, EPDMenuActions.EXPORT_PDF, EPDMenuActions.EXPORT_XML].includes(action)) {
      const availableExport: Export = availableExports.find((ex) => ex.key === action);
      this.downloadAttachment(availableExport.url);
      return;
    }
    this.dialog.open(EpdExportModalComponent, {
      data
    });
  }

  private exportEPD(exportType: EPDMenuActions) {
    // TODO
    // Basic exports to be fixed only if requested by Users
    this.toastrService.showMessage('Please wait, It will take few seconds to Export.', 3000);
    const filename = `Makersite_EPD Dashboard of ${this.data.name}`;
    if (exportType === EPDMenuActions.EXPORT_PDF) this.reusableService.export('epdDashboard', filename, 'pdf');
    if (exportType === EPDMenuActions.EXPORT_WORD) {
      this.reusableService.export('epdDashboard', filename, 'docx');
    }
  }
}
